/**
 * CanvasDomainConfigModal
 *
 * Lets users assign canvas templates to Market / Business / Solution domains.
 * Config is persisted in localStorage per project.
 */

import { useState } from 'react'
import { X, GripVertical, Check } from 'lucide-react'
import { CANVAS_TEMPLATES, DEFAULT_DOMAIN_TEMPLATES, type CanvasDomain, type CanvasDomainConfig } from './canvasTemplates'

const DOMAIN_LABELS: Record<CanvasDomain, string> = {
  market: 'Market',
  business: 'Business',
  solution: 'Solution',
}

const DOMAIN_COLORS: Record<CanvasDomain, string> = {
  market: 'var(--color-market)',
  business: 'var(--color-business)',
  solution: 'var(--color-solution)',
}

interface Props {
  config: CanvasDomainConfig
  onSave: (config: CanvasDomainConfig) => void
  onClose: () => void
}

export function CanvasDomainConfigModal({ config, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<CanvasDomainConfig>(() => ({
    market: [...config.market],
    business: [...config.business],
    solution: [...config.solution],
  }))

  const domains: CanvasDomain[] = ['market', 'business', 'solution']

  function toggle(domain: CanvasDomain, templateId: string) {
    setDraft(prev => {
      const current = prev[domain]
      const next = current.includes(templateId)
        ? current.filter(id => id !== templateId)
        : [...current, templateId]
      return { ...prev, [domain]: next }
    })
  }

  function handleReset() {
    setDraft({
      market: [...DEFAULT_DOMAIN_TEMPLATES.market],
      business: [...DEFAULT_DOMAIN_TEMPLATES.business],
      solution: [...DEFAULT_DOMAIN_TEMPLATES.solution],
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-warm-50 rounded-xl shadow-2xl w-[560px] max-h-[80vh] flex flex-col" style={{ boxShadow: '0 20px 60px rgba(29,26,21,0.18)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-200">
          <div>
            <h2 className="text-base font-semibold text-text font-display">Configure Canvas Templates</h2>
            <p className="text-xs text-text-muted mt-0.5">Choose which templates appear in each domain folder</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-warm-100 text-text-muted hover:text-text transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {domains.map(domain => (
            <div key={domain}>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: DOMAIN_COLORS[domain] }}
                />
                <span className="text-sm font-medium text-text">{DOMAIN_LABELS[domain]}</span>
                <span className="text-xs text-text-muted">
                  ({draft[domain].length} template{draft[domain].length !== 1 ? 's' : ''})
                </span>
              </div>

              <div className="space-y-1.5 pl-4">
                {CANVAS_TEMPLATES.map(template => {
                  const active = draft[domain].includes(template.id)
                  return (
                    <button
                      key={template.id}
                      onClick={() => toggle(domain, template.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        active
                          ? 'bg-white border border-warm-200 text-text shadow-sm'
                          : 'text-text-muted hover:bg-warm-100 hover:text-text border border-transparent'
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                          active ? 'text-white' : 'border border-warm-300'
                        }`}
                        style={active ? { background: DOMAIN_COLORS[domain] } : {}}
                      >
                        {active && <Check className="w-3 h-3" />}
                      </span>
                      <span className="flex-1">{template.name}</span>
                      <GripVertical className="w-3.5 h-3.5 text-warm-300 flex-shrink-0" />
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-warm-200">
          <button
            onClick={handleReset}
            className="text-xs text-text-muted hover:text-text underline underline-offset-2 transition-colors"
          >
            Reset to defaults
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-warm-200 text-text-secondary hover:bg-warm-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(draft)}
              className="px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors"
              style={{ background: 'var(--color-business)' }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
